import { useQuery } from '@tanstack/react-query'
import api from '../api'

// Types
export interface SymHubMetrics {
  totalPrompts: number
  totalAgents: number
  totalLikes: number
  totalPromptLikes: number
  totalAgentLikes: number
  totalCopies: number
}

export interface ModuleCompletionData {
  moduleId: number
  moduleName: string
  courseName?: string
  started: number
  completed: number
  rate: number
}

export interface CompletionTimeData {
  averageDays: number
  fastestDays: number
  slowestDays: number
  totalCompleted: number
}

export interface UserRankingItem {
  userId: string
  userName: string
  userEmail: string
  userAvatar?: string
  totalWatched: number
  totalCompleted: number
}

export interface AverageCourseProgress {
  averagePercent: number
  totalUsers: number
  totalCourses: number
  totalRegisteredUsers: number
}

export interface MetricsOverview {
  symHub: SymHubMetrics
  moduleCompletion: ModuleCompletionData[]
  completionTime: CompletionTimeData
}

/** Uma ou várias empresas. Array vazio/undefined = todas as empresas. */
export type CompanyFilter = string | string[] | undefined

/** Serializa company(s) pro query param `companyId` (CSV). undefined se vazio. */
export function serializeCompanyId(c: CompanyFilter): string | undefined {
  if (Array.isArray(c)) return c.length ? c.join(',') : undefined
  return c || undefined
}

/** Chave de cache estável pro React Query (ordena pra independer da ordem). */
export function companyKey(c: CompanyFilter): string | undefined {
  if (Array.isArray(c)) return c.length ? [...c].sort().join(',') : undefined
  return c || undefined
}

interface MetricsFilters {
  courseId?: number
  companyId?: CompanyFilter
}

function buildParams(filters: MetricsFilters) {
  const params: Record<string, string | number> = {}
  if (filters.courseId) params.courseId = filters.courseId
  const company = serializeCompanyId(filters.companyId)
  if (company) params.companyId = company
  return params
}

// Hooks
export function useSymHubMetrics() {
  return useQuery({
    queryKey: ['metrics', 'sym-hub'],
    queryFn: async () => {
      const response = await api.get<SymHubMetrics>('/metrics/sym-hub')
      return response.data
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export function useModuleCompletionMetrics() {
  return useQuery({
    queryKey: ['metrics', 'module-completion'],
    queryFn: async () => {
      const response = await api.get<ModuleCompletionData[]>('/metrics/module-completion')
      return response.data
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export function useCompletionTimeMetrics() {
  return useQuery({
    queryKey: ['metrics', 'completion-time'],
    queryFn: async () => {
      const response = await api.get<CompletionTimeData>('/metrics/completion-time')
      return response.data
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export function useMetricsOverview(courseId?: number, companyId?: CompanyFilter) {
  return useQuery({
    queryKey: ['metrics', 'overview', courseId, companyKey(companyId)],
    queryFn: async () => {
      const params = buildParams({ courseId, companyId })
      const response = await api.get<MetricsOverview>('/metrics/overview', { params })
      return response.data
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

export function useUserRanking(courseId?: number, companyId?: CompanyFilter) {
  return useQuery({
    queryKey: ['metrics', 'user-ranking', courseId, companyKey(companyId)],
    queryFn: async () => {
      const params = buildParams({ courseId, companyId })
      const response = await api.get<UserRankingItem[]>('/metrics/user-ranking', { params })
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

export function useAverageCourseProgress(courseId?: number, companyId?: CompanyFilter) {
  return useQuery({
    queryKey: ['metrics', 'average-course-progress', courseId, companyKey(companyId)],
    queryFn: async () => {
      const params = buildParams({ courseId, companyId })
      const response = await api.get<AverageCourseProgress>('/metrics/average-course-progress', {
        params,
      })
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

// ─── Quiz Analytics ────────────────────────────────────────────────────────

export interface QuizAnalytics {
  averageScoreByModule: {
    moduleId: number
    moduleName: string
    averageScore: number
    totalAttempts: number
  }[]
  completionByModuleAndEpisode: {
    moduleId: number
    moduleName: string
    episodes: {
      episodeId: number
      episodeTitle: string
      quizTitle: string
      totalEnrolled: number
      totalAttempted: number
      completionRate: number
    }[]
  }[]
  avgResponseTimeByQuestion: {
    questionId: number
    questionText: string
    quizTitle: string
    avgTimeMs: number
    totalResponses: number
  }[]
  attemptsByQuiz: {
    quizId: number
    quizTitle: string
    episodeTitle: string
    moduleName: string
    totalAttempts: number
    averageScore: number
    highestScore: number
    lowestScore: number
  }[]
}

export function useQuizAnalytics(courseId?: number, companyId?: CompanyFilter) {
  return useQuery({
    queryKey: ['metrics', 'quiz-analytics', courseId, companyKey(companyId)],
    queryFn: async () => {
      const params = buildParams({ courseId, companyId })
      const response = await api.get<QuizAnalytics>('/metrics/quiz-analytics', { params })
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

// ─── Course Feedbacks ──────────────────────────────────────────────────────

export interface FeedbackItem {
  id: number
  rating: number
  comment: string
  createdAt: string
  userName: string
  userAvatar: string | null
  courseName: string
  courseId: number
}

export interface CourseFeedbacksData {
  averageRating: number
  totalFeedbacks: number
  distribution: Record<number, number>
  feedbacks: FeedbackItem[]
}

export function useCourseFeedbacks(filters: {
  courseId?: number
  companyId?: CompanyFilter
  dateFrom?: string
  dateTo?: string
}) {
  return useQuery({
    queryKey: [
      'metrics',
      'course-feedbacks',
      filters.courseId,
      companyKey(filters.companyId),
      filters.dateFrom,
      filters.dateTo,
    ],
    queryFn: async () => {
      const params: Record<string, string | number> = {}
      if (filters.courseId) params.courseId = filters.courseId
      const company = serializeCompanyId(filters.companyId)
      if (company) params.companyId = company
      if (filters.dateFrom) params.dateFrom = filters.dateFrom
      if (filters.dateTo) params.dateTo = filters.dateTo
      const response = await api.get<CourseFeedbacksData>('/metrics/course-feedbacks', { params })
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

// ──────────────────────────────────────────────────────────────────────────
// Dashboard Analytics (D1-D5) — endpoints novos do super_admin dashboard

export interface LmsHealth {
  users: { total: number; withCompany: number; active30d: number; new7d: number }
  enrollment: {
    totalEnrollments: number
    completed: number
    inProgress: number
    completionRate: number
  }
  badges: { totalGrants: number; distinctUsers: number; avgPerUser: number }
  engagement: {
    episodesCompletedTotal: number
    quizzesCompletedTotal: number
    averageQuizScore: number
    averageRating: number
  }
}

export function useLmsHealth(companyId?: CompanyFilter) {
  return useQuery({
    queryKey: ['metrics', 'lms-health', companyKey(companyId)],
    queryFn: async () => {
      const params: Record<string, string> = {}
      const company = serializeCompanyId(companyId)
      if (company) params.companyId = company
      const response = await api.get<LmsHealth>('/metrics/lms-health', { params })
      return response.data
    },
    staleTime: 1000 * 60 * 2,
  })
}

export interface ActivityBucket {
  date: string
  enrollments: number
  episodesCompleted: number
  quizzesCompleted: number
  badgesGranted: number
}

export interface ActivityTimeline {
  buckets: ActivityBucket[]
  groupBy: 'day' | 'week' | 'month'
  dateFrom: string
  dateTo: string
}

export function useActivityTimeline(opts: {
  groupBy?: 'day' | 'week' | 'month'
  dateFrom?: string
  dateTo?: string
  companyId?: CompanyFilter
}) {
  return useQuery({
    queryKey: [
      'metrics',
      'activity-timeline',
      opts.groupBy,
      opts.dateFrom,
      opts.dateTo,
      companyKey(opts.companyId),
    ],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (opts.groupBy) params.groupBy = opts.groupBy
      if (opts.dateFrom) params.dateFrom = opts.dateFrom
      if (opts.dateTo) params.dateTo = opts.dateTo
      const company = serializeCompanyId(opts.companyId)
      if (company) params.companyId = company
      const response = await api.get<ActivityTimeline>('/metrics/activity-timeline', { params })
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

export interface HeatmapCell {
  weekday: number // 1=domingo..7=sábado (MariaDB DAYOFWEEK)
  hour: number // 0-23
  count: number
}

export interface ActivityHeatmap {
  cells: HeatmapCell[]
  dateFrom: string
}

export function useActivityHeatmap(opts: { companyId?: CompanyFilter; dateFrom?: string }) {
  return useQuery({
    queryKey: ['metrics', 'activity-heatmap', companyKey(opts.companyId), opts.dateFrom],
    queryFn: async () => {
      const params: Record<string, string> = {}
      const company = serializeCompanyId(opts.companyId)
      if (company) params.companyId = company
      if (opts.dateFrom) params.dateFrom = opts.dateFrom
      const response = await api.get<ActivityHeatmap>('/metrics/activity-heatmap', { params })
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

export interface CompanyAnalyticsRow {
  companyId: string
  companyName: string
  logo: string | null
  usersCount: number
  enrollmentsCompleted: number
  enrollmentsTotal: number
  completionRate: number
  totalBadgesGranted: number
  avgBadgesPerUser: number
  averageRating: number
}

export function useCompaniesAnalytics(companyId?: CompanyFilter) {
  return useQuery({
    queryKey: ['metrics', 'by-company', companyKey(companyId)],
    queryFn: async () => {
      const params: Record<string, string> = {}
      const company = serializeCompanyId(companyId)
      if (company) params.companyId = company
      const response = await api.get<CompanyAnalyticsRow[]>('/metrics/by-company', { params })
      return response.data
    },
    staleTime: 1000 * 60 * 5,
  })
}

export interface ModuleFunnelStep {
  moduleId: number
  moduleName: string
  totalEpisodes: number
  started: number
  completed: number
  dropToNext: number
}

export interface ModuleFunnel {
  courseId: number
  courseName: string
  steps: ModuleFunnelStep[]
}

export interface BadgeDistributionRow {
  badgeId: string
  badgeName: string
  tier: 'bronze' | 'prata' | 'ouro' | 'platina'
  criterionType: string
  grants: number
}

export function useBadgesDistribution(companyId?: CompanyFilter) {
  return useQuery({
    queryKey: ['metrics', 'badges-distribution', companyKey(companyId)],
    queryFn: async () => {
      const params: Record<string, string> = {}
      const company = serializeCompanyId(companyId)
      if (company) params.companyId = company
      const response = await api.get<BadgeDistributionRow[]>('/metrics/badges-distribution', {
        params,
      })
      return response.data
    },
    staleTime: 1000 * 60 * 2,
  })
}

export function useModuleFunnel(opts: { courseId?: number; companyId?: CompanyFilter }) {
  return useQuery({
    queryKey: ['metrics', 'module-funnel', opts.courseId, companyKey(opts.companyId)],
    queryFn: async () => {
      if (!opts.courseId) return null
      const params: Record<string, string | number> = { courseId: opts.courseId }
      const company = serializeCompanyId(opts.companyId)
      if (company) params.companyId = company
      const response = await api.get<ModuleFunnel>('/metrics/module-funnel', { params })
      return response.data
    },
    enabled: !!opts.courseId,
    staleTime: 1000 * 60 * 5,
  })
}
